import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Autocomplete, FormControlLabel, Box, Typography, Stack, InputAdornment, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { getSuppliers } from '../../services/SupplierService';
import { getCategoriesByType } from '../../services/CategoryService';
import { unitOptions, categoryOptions } from '../../utils/Enums';
import IOSSwitch from '../Switch/IOSSwitch';

import CurrencySelect from '../Currency/CurrencySelect';
const EditSupplyModal = ({ open, onClose, onSave, supply }) => {
  
  const { t } = useTranslation();

  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedPrimarySupplier, setSelectedPrimarySupplier] = useState(null);
  const [selectedSecondarySupplier, setSelectedSecondarySupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    stock: '',
    minimumStock: '',
    suggestedStock: '',
    price: '',
    conversionFactor: '',
    primarySupplier: '',
    secondarySupplier: '',
    notes: '',
    alternative: '',
    category: '',
    subCategory: '',
    presentationQuantity: '',
    iva: '',
    usesLot: true,
    countsStock: true
  });

  useEffect(() => {
    if (open) {
      Promise.all([
        getSuppliers(1, 10000), // Cargar todos los proveedores con paginación grande
        getCategoriesByType(categoryOptions.SUPPLIES)
      ]).then(([suppliersData, categoriesData]) => {
        // Asegurar que suppliersData sea un array
        const suppliersArray = Array.isArray(suppliersData) ? suppliersData : (suppliersData?.providers || suppliersData?.data || []);
        setSuppliers(suppliersArray);
        setCategories(categoriesData);
    
        if (supply) {
          const primary = suppliersArray.find(s => s.id === supply.primarySupplier.id);
          const secondary = supply.secondarySupplier ? suppliersArray.find(s => s.id === supply.secondarySupplier.id) : null;
          const category = categoriesData.find(c => c.id === supply.category.id);
          const subCategory = category?.subCategories.find(sc => sc.id === supply.subCategory.id);
  
          setSelectedPrimarySupplier(primary || null);
          setSelectedSecondarySupplier(secondary || null);
  
          setFormData({
            ...supply,
            stock: supply.stock ?? '',
            minimumStock: supply.minimumStock ?? '',
            suggestedStock: supply.suggestedStock ?? '',
            primarySupplier: primary?.id || '',
            secondarySupplier: secondary?.id || '',
            category: category?.id || '',
            subCategory: subCategory?.id || ''
          });
        }
      });
    }
  }, [supply, open]);

  const handleCategoryChange = (event) => {
    const { value } = event.target;
    setFormData({ ...formData, category: value, subCategory: '' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' }); // Clear errors on change
  };

  const handleSave = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length === 0) {
      onSave({
        ...formData
      });
      onClose();
    } else {
      setErrors(validationErrors);
    }
  };

  const validateForm = () => {
    let validationErrors = {};
    if (!formData.name.trim()) {
        validationErrors.name = t('name_required');
    }
    if (!formData.unit) {
        validationErrors.unit = t('unit_required');
    }
    if (formData.countsStock && (formData.stock === '' || formData.stock === null || formData.stock === undefined)) {
        validationErrors.stock = t('stock_required');
    } else if (formData.countsStock && (isNaN(formData.stock) || formData.stock < 0)) {
        validationErrors.stock = t('stock_must_positive_required');
    }
    if (!formData.conversionFactor) {
        validationErrors.conversionFactor = t('conversion_factor_required');
    } else if (isNaN(formData.conversionFactor) || formData.conversionFactor <= 0) {
        validationErrors.conversionFactor = t('conversion_factor_must_greater_required');
    }
    formData.primarySupplier = selectedPrimarySupplier?.id
    formData.secondarySupplier = selectedSecondarySupplier?.id || null
    if (!formData.primarySupplier) {
        validationErrors.primarySupplier = t('primary_supplier_is_required');
    }
    // secondarySupplier es opcional, no se valida
    if (!formData.category) {
        validationErrors.category = t('Category is required');
    }
    if (!formData.subCategory) {
        validationErrors.subCategory = t('subcategory_is_required');
    }
    if (!formData.presentationQuantity) {
        validationErrors.presentationQuantity = t('presentation_quantity_is_required');
    } else if (isNaN(formData.presentationQuantity) || formData.presentationQuantity <= 0) {
        validationErrors.presentationQuantity = t('presentation_quantity_must_greater_is_required');
    }
    if (formData.iva === '' || formData.iva === null || formData.iva === undefined) {
        validationErrors.iva = t('iva_is_required');
    } else if (isNaN(formData.iva) || Number(formData.iva) < 0) {
        validationErrors.iva = t('iva_must_greater_is_required');
    }
    return validationErrors;
  };

  const sectionTitleSx = {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#5f6b7a',
    mb: 1.5
  };

  const switchCardSx = {
    border: '1px solid #dbe3ec',
    borderRadius: 3,
    px: 2,
    pt: 1.5,
    pb: 1,
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'flex-start'
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontSize: 28, fontWeight: 700 }}>{t('edit_supply')}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Typography sx={sectionTitleSx}>{t('general_information', { defaultValue: 'Informacion general' })}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
            <TextField
              label={t('name')}
              name="name"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
              <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label={t('cost')}
              name="price"
              fullWidth
              value={formData.price}
              onChange={handleChange}
              error={!!errors.price}
              helperText={errors.price}
            />
            <CurrencySelect
              size="medium"
              value={formData.costCurrency}
              onChange={(costCurrency) => setFormData({ ...formData, costCurrency })}
              sx={{ minWidth: 150 }}
            />
            </Box>
          </Grid>
              <Grid item xs={12} md={6}>
            <TextField
              label={t('iva')}
              name="iva"
              type='number'
              fullWidth
              value={formData.iva}
              onChange={handleChange}
              error={!!errors.iva}
              helperText={errors.iva}
            />
          </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('notes')}
                  name="notes"
                  fullWidth
                  value={formData.notes}
                  onChange={handleChange}
                  error={!!errors.notes}
                  helperText={errors.notes}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('alternative')}
                  name="alternative"
                  fullWidth
                  value={formData.alternative}
                  onChange={handleChange}
                  error={!!errors.alternative}
                  helperText={errors.alternative}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography sx={sectionTitleSx}>{t('stock_configuration', { defaultValue: 'Configuracion de stock' })}</Typography>
            <Grid container spacing={2.5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Box sx={switchCardSx}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      width: '100%',
                      alignItems: 'flex-start',
                      '& .MuiFormControlLabel-label': { flex: 1 },
                      '& .MuiSwitch-root': { mr: 1.5 }
                    }}
                    control={<IOSSwitch checked={!!formData.usesLot} onChange={(e) => setFormData(prev => ({ ...prev, usesLot: e.target.checked }))} />}
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1f2937' }}>{t('uses_lot')}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.25 }}>
                          {t('uses_lot_description', { defaultValue: 'Si esta activo, el sistema pedira lote y vencimiento en reposiciones y consumos.' })}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={switchCardSx}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      width: '100%',
                      alignItems: 'flex-start',
                      '& .MuiFormControlLabel-label': { flex: 1 },
                      '& .MuiSwitch-root': { mr: 1.5 }
                    }}
                    control={<IOSSwitch checked={!!formData.countsStock} onChange={(e) => setFormData(prev => ({ ...prev, countsStock: e.target.checked }))} />}
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#1f2937' }}>{t('counts_stock')}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.25 }}>
                          {t('counts_stock_description', { defaultValue: 'Si esta desactivado, no acumula stock y en tablas se mostrara como no controlado.' })}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('stock')}
                  name="stock"
              type='number'
              fullWidth
              value={formData.stock}
              onChange={handleChange}
              error={!!errors.stock}
              helperText={errors.stock}
              disabled={!formData.countsStock}
            />
          </Grid>
              <Grid item xs={12} md={4}>
            <TextField
              label={t('minimum_stock')}
              name="minimumStock"
              type='number'
              fullWidth
              value={formData.minimumStock}
              onChange={handleChange}
              error={!!errors.minimumStock}
              helperText={errors.minimumStock}
              disabled={!formData.countsStock}
            />
          </Grid>
              <Grid item xs={12} md={4}>
            <TextField
              label={t('suggested_stock')}
              name="suggestedStock"
              type='number'
              fullWidth
              value={formData.suggestedStock}
              onChange={handleChange}
              error={!!errors.suggestedStock}
              helperText={errors.suggestedStock}
                  disabled={!formData.countsStock}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography sx={sectionTitleSx}>{t('presentation', { defaultValue: 'Presentacion' })}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="unit-label">{t('unit')}</InputLabel>
              <Select
                labelId="unit-label"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                label={t('unit')}
              >
                {unitOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
              <Grid item xs={12} md={4}>
            <TextField
              label={t('conversion_factor')}
              name="conversionFactor"
              fullWidth
              value={formData.conversionFactor}
              onChange={handleChange}
              error={!!errors.conversionFactor}
              helperText={errors.conversionFactor}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={t('conversion_factor_helper')}>
                      <IconButton
                        edge="end"
                        size="small"
                        tabIndex={-1}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          flex: '0 0 32px'
                        }}
                      >
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
              <Grid item xs={12} md={4}>
            <TextField
              label={t('presentation_quantity')}
              name="presentationQuantity"
              fullWidth
              value={formData.presentationQuantity}
              onChange={handleChange}
              error={!!errors.presentationQuantity}
              helperText={errors.presentationQuantity}
            />
          </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography sx={sectionTitleSx}>{t('classification_and_suppliers', { defaultValue: 'Clasificacion y proveedores' })}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedPrimarySupplier}
              onChange={(event, newValue) => setSelectedPrimarySupplier(newValue)}
              options={suppliers}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label={t('primary_supplier')} />}
              fullWidth
            />
          </Grid>
              <Grid item xs={12} md={6}>
            <Autocomplete
              value={selectedSecondarySupplier}
              onChange={(event, newValue) => setSelectedSecondarySupplier(newValue)}
              options={suppliers}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label={t('secondary_supplier')} />}
              fullWidth
            />
          </Grid>
              <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('category')}</InputLabel>
              <Select
                value={formData.category}
                onChange={handleCategoryChange}
                label={t('category')}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
              <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('sub_category')}</InputLabel>
              <Select
                value={formData.subCategory}
                onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                label={t('sub_category')}
                disabled={!formData.category}
              >
                {categories.find(cat => cat.id === formData.category)?.subCategories.map(sub => (
                  <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSupplyModal;
